import React, { useEffect, Suspense, lazy } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { QRModalProvider } from "./context/QRModalContext";
import { AuthProvider } from "./context/AuthContext";
import RequireAdmin from "./components/auth/RequireAdmin";
import QRCodeModal from "./components/generals/QRCodeModal";
import Header from "./components/generals/Header";
import AppOpen from "./components/generals/AppOpen";
import Mission from "./components/about/Mission";
import FAQ from "./components/generals/FAQ";
import EditorialProcess from "./components/generals/EditorialProcess";
import Footer from "./components/generals/Footer";
import Home from "./components/generals/Home";
import Menu from "./components/generals/Menu";
import TermsOfService from "./components/generals/TermsOfService";
import PrivacyPolicy from "./components/generals/PrivacyPolicy";
import HerbCategories from "./components/herbs/HerbCategories";
import HerbCategoryList from "./components/herbs/HerbCategoryList";
import HerbPage from "./components/herbs/HerbPage";
import ConditionPage from "./components/conditions/ConditionPage";
import "./App.css";
import LibraryHome from "./components/library/LibraryHome";
import ArticleDisplay from "./components/library/ArticleDisplay";
import ArticleComponent from "./components/library/ArticleComponent";
import DbArticle from "./components/library/DbArticle";
import CategoryPage from "./components/library/CategoryPage";
import DoshaQuiz from "./components/quizzes/DoshaQuiz";
import DoshaResult from "./components/quizzes/DoshaResult";
import QuizzesHub from "./components/quizzes/QuizzesHub";
import Quiz from "./components/quizzes/Quiz";
import QuizResult from "./components/quizzes/QuizResult";
import Login from "./components/login/Login";
import AppWaitList from "./components/generals/AppWaitList";
import Contact from "./components/generals/Contact";
import SignUp from "./components/login/SignUp";
import ForgotPassword from "./components/login/ForgotPassword";
import Options from "./components/enrollment/Options";
import QueryForm from "./components/library/QueryForm";
import ArticleCategories from "./components/library/ArticleCategories";
import { SubscriptionProvider } from "./components/payment/SubscriptionContext";
import Careers from "./components/practitioners/Careers";

import DemoRequest from "./components/business/DemoRequest";
import AffiliateRequest from "./components/business/AffiliateRequest";
import AffiliateDashboard from "./components/affiliates/AffiliateDashboard";
import RequireAffiliate from "./components/affiliates/RequireAffiliate";
import InfluencerRedirect from "./components/affiliates/InfluencerRedirect";

import Services from "./components/services/Services";

// const PersonalizedRecipes = lazy(() => import("./components/explorer/recommedations/PersonalizedRecipes"));

// import ReactGA from "react-ga";
import ReactGA from "react-ga4";
import SEO from "./components/utils/SEO";

// The ONLY lazily-loaded route in the app. Every other component above is
// imported eagerly into one bundle that every visitor downloads, including on
// the herb SEO pages. The Atlas is a big interactive view that almost nobody
// arriving from search will open, so it ships as its own chunk. It is also
// excluded from the prerender allow-list (scripts/prerender-pages.js), which
// means the Suspense fallback below never has to match prerendered markup —
// the reason lazy loading is safe here and not on the prerendered routes.

// Same reasoning, same safety argument: /conditions is not in the prerender
// allow-list either, so a Suspense fallback here can never mismatch prerendered
// markup. It is split because it now carries the baked 734-condition index
// (~75 kB of JSON) — imported eagerly that would be added to the bundle every
// visitor downloads, including on the herb SEO pages, to serve one hub page.
const ConditionsIndex = lazy(() =>
  import("./components/conditions/ConditionsIndex")
);

// Two more splits, for weight rather than for interactivity. Neither route is
// in the prerender allow-list, so the Suspense fallbacks cannot mismatch
// prerendered markup — the same safety argument as the Atlas.
//
// /video-wrapper pulls agora-rtc-react, and /start pulls @revenuecat/purchases-js
// plus lottie-web. Imported eagerly, all three sat in the bundle that every
// visitor downloads before the page can issue its first query — including
// someone who landed on a herb page from search and will never open either one.
const VideoCallWrapper = lazy(() =>
  import("./components/video-call/VideoCallWrapper")
);
const OnboardingFunnel = lazy(() =>
  import("./components/onboarding/OnboardingFunnel")
);
const MeetingRoom = lazy(() => import("./components/video-call/MeetingRoom"));
// Legacy Firestore-backed route, not prerendered — splitting it takes the
// firebase SDK out of the main bundle for everyone who never opens it.
const Article = lazy(() => import("./components/library/Article"));
// Both import firebase and neither route is prerendered — /quizComponent was
// kept eager by mistake on the first pass (it is not in the prerender list),
// and MaraljsonManager is an internal JSON tool behind /MaraljsonManager.
const QuizComponent = lazy(() =>
  import("./components/symptom-checker/QuizComponent")
);
const MaraljsonManager = lazy(() =>
  import("./components/utils/MaraljsonManager")
);

// Shared fallback for the weight-split routes above.
const LazyRoute = ({ children, label }) => (
  <Suspense
    fallback={<div style={{ padding: "80px 20px" }}>Loading {label}…</div>}
  >
    {children}
  </Suspense>
);

// GA4 property for Firebase project cedar-18da3 — must match the gtag ID in
// public/index.html and ReactGA.initialize in src/index.js. usePageTracking below
// sends every SPA route change here, so a wrong ID loses all navigation data.

// ---------------------------------------------------------------- code split
// Route trees no public page reaches: the enrollment funnel, admin, the
// practitioner and appointment areas, user profile, symptom checker, explorer
// and payment. Between them they own firebase (~64 refs) and moment, which
// every visitor was downloading before a herb page could issue its first
// query.
//
// Everything a prerendered route renders stays EAGERLY imported above. That is
// the invariant: index.js hydrates prerendered markup, so if a route's
// component suspended, the fallback would replace HTML React is trying to
// match and hydration would break. /options and /careers are the two that look
// splittable but are not -- both are prerendered AND use firebase.
const AddHerbs = lazy(() => import("./components/admin/herbs/AddHerbs"));
const AddProduct = lazy(() => import("./components/admin/products/AddProduct"));
const AddRecipe = lazy(() => import("./components/admin/recipes/AddRecipe"));
const AdminDashboard = lazy(() => import("./components/admin/AdminDashboard"));
const AdminOnboardingFunnel = lazy(() =>
  import("./components/admin/analytics/OnboardingFunnel")
);
const AffiliateApplications = lazy(() =>
  import("./components/admin/affiliates/AffiliateApplications")
);
const AffiliateNetworks = lazy(() =>
  import("./components/admin/affiliates/AffiliateNetworks")
);
const Age = lazy(() => import("./components/enrollment/Age"));
const Alcohol = lazy(() => import("./components/enrollment/Alcohol"));
const Allergies = lazy(() => import("./components/enrollment/Allergies"));
const AppWaitlistAdmin = lazy(() =>
  import("./components/admin/leads/AppWaitlistAdmin")
);
const ApplicationComplete = lazy(() =>
  import("./components/practitioners/signup/ApplicationComplete")
);
const AppointmentCompletion = lazy(() =>
  import("./components/appointment/payment/AppointmentCompletion")
);
const AppointmentManagement = lazy(() =>
  import("./components/practitioners/management/AppointmentManagement")
);
const ArticleEditor = lazy(() =>
  import("./components/admin/articles/ArticleEditor")
);
const ArticlesAdmin = lazy(() =>
  import("./components/admin/articles/ArticlesAdmin")
);
const AtlasPage = lazy(() => import("./components/atlas/AtlasPage"));
const Availability = lazy(() =>
  import("./components/practitioners/signup/Availability")
);
const Background = lazy(() =>
  import("./components/practitioners/signup/Background")
);
const CategoriesAdmin = lazy(() =>
  import("./components/admin/articles/CategoriesAdmin")
);
const ChronicDiseases = lazy(() =>
  import("./components/enrollment/ChronicDiseases")
);
const ChronicPain = lazy(() => import("./components/enrollment/ChronicPain"));
const ClientsInfo = lazy(() =>
  import("./components/practitioners/clients/ClientsInfo")
);
const ClinetsDetails = lazy(() =>
  import("./components/practitioners/clients/ClinetsDetails")
);
const Completion = lazy(() => import("./components/payment/stripe/Completion"));
const ContactMessages = lazy(() =>
  import("./components/admin/contact/ContactMessages")
);
const ConvertFieldToArray = lazy(() =>
  import("./components/admin/tools/ConvertFieldToArray")
);
const CurrentMedication = lazy(() =>
  import("./components/enrollment/CurrentMedication")
);
const Dashboard = lazy(() => import("./components/explorer/Dashboard"));
const Decryptor = lazy(() => import("./components/admin/users-mng/Decryptor"));
const DemoRequestsAdmin = lazy(() =>
  import("./components/admin/leads/DemoRequestsAdmin")
);
const Diet = lazy(() => import("./components/enrollment/Diet"));
const DietRating = lazy(() => import("./components/enrollment/DietRating"));
const DirectDepositInfo = lazy(() =>
  import("./components/practitioners/signup/DirectDepositInfo")
);
const DiseaseCategories = lazy(() =>
  import("./components/symptom-checker/DiseaseCategories")
);
const DrVisits = lazy(() => import("./components/enrollment/DrVisits"));
const Exercise = lazy(() => import("./components/enrollment/Exercise"));
const Expertise = lazy(() =>
  import("./components/practitioners/signup/Expertise")
);
const FamilyHistory = lazy(() =>
  import("./components/enrollment/FamilyHistory")
);
const FruitsVeggies = lazy(() =>
  import("./components/enrollment/FruitsVeggies")
);
const Gender = lazy(() => import("./components/enrollment/Gender"));
const GeneralInfo = lazy(() =>
  import("./components/practitioners/signup/GeneralInfo")
);
const HealthGoal = lazy(() => import("./components/enrollment/HealthGoal"));
const HealthIssues = lazy(() => import("./components/enrollment/HealthIssues"));
const HerbalistForm = lazy(() =>
  import("./components/practitioners/signup/HerbalistForm")
);
const HerbsAll = lazy(() =>
  import("./components/explorer/herb-categories/HerbsAll")
);
const HerbsDetails = lazy(() =>
  import("./components/admin/herbs/HerbsDetails")
);
const HerbsGrid = lazy(() =>
  import("./components/explorer/herb-categories/HerbsGrid")
);
const HerbsInfo = lazy(() => import("./components/admin/herbs/HerbsInfo"));
const HowDidYouHearAboutUs = lazy(() =>
  import("./components/enrollment/HowDidYouHearAboutUs")
);
const MeetingNote = lazy(() =>
  import("./components/practitioners/management/MeetingNote")
);
const MeetingsManagement = lazy(() =>
  import("./components/practitioners/management/MeetingsManagement")
);
const MembersScheduleAppointments = lazy(() =>
  import("./components/appointment/MembersScheduleAppointments")
);
const MentalHealthSatisfaction = lazy(() =>
  import("./components/enrollment/MentalHealthSatisfaction")
);
const MentalHealthStatus = lazy(() =>
  import("./components/enrollment/MentalHealthStatus")
);
const MobilityIssues = lazy(() =>
  import("./components/enrollment/MobilityIssues")
);
const Order = lazy(() => import("./components/payment/Order"));
const PastAppointments = lazy(() =>
  import("./components/user-profile/appointments-history/PastAppointments")
);
const Payment = lazy(() => import("./components/payment/stripe/Payment"));
const PhysicalHealthSatisfaction = lazy(() =>
  import("./components/enrollment/PhysicalHealthSatisfaction")
);
const PhytotherapyIndex = lazy(() =>
  import("./components/admin/herbs/PhytotherapyIndex")
);
const PractitionerInfo = lazy(() =>
  import("./components/admin/practitioners-mng/PractitionerInfo")
);
const PractitionerProfile = lazy(() =>
  import("./components/practitioners/profile/PractitionerProfile")
);
const PractitionerProfileDetails = lazy(() =>
  import("./components/appointment/PractitionerProfileDetails")
);
const PractitionerSettings = lazy(() =>
  import("./components/practitioners/PractitionerSettings")
);
const PractitionersDashboard = lazy(() =>
  import("./components/practitioners/PractitionersDashboard")
);
const PractitionersDetails = lazy(() =>
  import("./components/admin/practitioners-mng/PractitionersDetails")
);
const PregnancyStatus = lazy(() =>
  import("./components/enrollment/PregnancyStatus")
);
const ProductDiagnostic = lazy(() =>
  import("./components/admin/products/ProductDiagnostic")
);
const ProductReview = lazy(() =>
  import("./components/admin/products/ProductReview")
);
const ProfileDescription = lazy(() =>
  import("./components/practitioners/signup/ProfileDescription")
);
const ProfilePhoto = lazy(() =>
  import("./components/practitioners/signup/ProfilePhoto")
);
const QuizLeadsAdmin = lazy(() =>
  import("./components/admin/leads/QuizLeadsAdmin")
);
const RecipeDetails = lazy(() =>
  import("./components/admin/recipes/RecipeDetails")
);
const Recipes = lazy(() =>
  import("./components/explorer/recipes-categories/Recipes")
);
const RecipesAll = lazy(() =>
  import("./components/explorer/recipes-categories/RecipesAll")
);
const RecipesInfo = lazy(() =>
  import("./components/admin/recipes/RecipesInfo")
);
const RemedyRecommendation = lazy(() =>
  import("./components/symptom-checker/RemedyRecommendation")
);
const Rescheduling = lazy(() =>
  import("./components/appointment/Rescheduling")
);
const ScheduleAppointment = lazy(() =>
  import("./components/appointment/ScheduleAppointment")
);
const ScreenTime = lazy(() => import("./components/enrollment/ScreenTime"));
const Sleep = lazy(() => import("./components/enrollment/Sleep"));
const SleepDisorders = lazy(() =>
  import("./components/enrollment/SleepDisorders")
);
const Smoke = lazy(() => import("./components/enrollment/Smoke"));
const Stress = lazy(() => import("./components/enrollment/Stress"));
const StressSymptoms = lazy(() =>
  import("./components/enrollment/StressSymptoms")
);
const SubscriptionOptions = lazy(() =>
  import("./components/payment/SubscriptionOptions")
);
const SubscriptionPayment = lazy(() =>
  import("./components/payment/SubscriptionPayment")
);
const UniqueCategories = lazy(() =>
  import("./components/admin/tools/UniqueCategories")
);
const UpcomingAppointments = lazy(() =>
  import("./components/user-profile/appointments-history/UpcomingAppointments")
);
const UserProfile = lazy(() => import("./components/user-profile/UserProfile"));
const UserSettings = lazy(() =>
  import("./components/user-profile/UserSettings")
);
const UsersInfo = lazy(() => import("./components/admin/users-mng/UsersInfo"));
const ViewPractitioners = lazy(() =>
  import("./components/appointment/ViewPractitioners")
);
const WaterIntake = lazy(() => import("./components/enrollment/WaterIntake"));
const Weight = lazy(() => import("./components/enrollment/Weight"));
const Welcome = lazy(() => import("./components/practitioners/Welcome"));
const WellnessPractices = lazy(() =>
  import("./components/enrollment/WellnessPractices")
);

const trackingId = "G-0M1S0Y2WMR";

ReactGA.initialize(trackingId);

function usePageTracking() {
  const location = useLocation();

  useEffect(() => {
    ReactGA.send({
      hitType: "pageview",
      page: location.pathname + location.search,
    });
  }, [location]);
}

const PRIVATE_PATHS = [
  "/maral1362",
  "/herbs-info",
  "/add-herbs",
  "/phytotherapy-index",
  "/users-info",
  "/decryptor",
  "/add-recipe",
  "/recipes-info",
  "/to-array",
  "/unique-categories",
  "/practitioners-info",
  "/product-review",
  "/add-product",
  "/product-diagnostic",
  "/practitioners-dashboard",
  "/appointments-mng",
  "/meetings-mng",
  "/meeting-notes",
  "/clients-info",
  "/user-settings",
  "/user-profile",
  "/upcoming-visits",
  "/past-visits",
  "/practitioner-settings",
  "/welcome",
  "/affiliate-login",
  "/affiliate-dashboard",
  "/admin/affiliate-applications",
  "/admin/affiliate-networks",
  "/admin/onboarding-funnel",
  "/admin/contact-messages",
  "/admin/articles",
  "/admin/categories",
  "/admin/app-waitlist",
  "/admin/quiz-leads",
  "/admin/demo-requests",
  "/app",
  "/r/",
];

const NoIndexWrapper = () => {
  const location = useLocation();
  const isPrivate = PRIVATE_PATHS.some((path) =>
    location.pathname.startsWith(path)
  );
  if (!isPrivate) return null;
  return <SEO noIndex={true} />;
};

const Layout = ({ children }) => {
  usePageTracking();
  return (
    <div role="application">
      <NoIndexWrapper />
      {children}
    </div>
  );
};

const FooterWrapper = () => {
  const location = useLocation();
  const noFooterPaths = [
    "/video-room",
    "/meeting-room",
    "/video-wrapper",
    "/meeting-notes",
    "/start",
  ];
  if (noFooterPaths.includes(location.pathname)) {
    return null;
  }
  return <Footer />;
};

// The site nav renders globally, but full-screen flows (the onboarding funnel,
// video/meeting rooms) own the whole viewport — its white logo was floating over
// the funnel's progress bar. Hide it on those routes, same as the footer.
const MenuWrapper = () => {
  const location = useLocation();
  const noMenuPaths = [
    "/video-room",
    "/meeting-room",
    "/video-wrapper",
    "/meeting-notes",
    "/start",
  ];
  if (noMenuPaths.includes(location.pathname)) {
    return null;
  }
  return <Menu />;
};

function App() {
  return (
    <Router>
      <AuthProvider>
        <QRModalProvider>
          <SubscriptionProvider>
            <Layout>
              <MenuWrapper />
              <Suspense
                fallback={<div style={{ padding: "80px 20px" }}>Loading…</div>}
              >
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/learn" element={<LibraryHome />} />
                  <Route path="/article/:id" element={<Article />} />
                  <Route
                    path="/article-categories"
                    element={<ArticleCategories />}
                  />
                  <Route path="/QueryForm" element={<QueryForm />} />
                  <Route
                    path="/MaraljsonManager"
                    element={<MaraljsonManager />}
                  />
                  <Route path="/get-started" element={<Age />} />
                  {/* New Noom/Calm-style web onboarding funnel (mobile-app parity + affiliate handoff). */}
                  <Route
                    path="/start"
                    element={
                      <LazyRoute label="Sârv">
                        <OnboardingFunnel />
                      </LazyRoute>
                    }
                  />
                  {/* App-handoff universal-link target (magic link → app, or install fallback). */}
                  <Route path="/app" element={<AppOpen />} />
                  <Route path="/careers" element={<Careers />} />
                  <Route path="/expertise" element={<Expertise />} />
                  <Route path="/background" element={<Background />} />
                  <Route path="/general-info" element={<GeneralInfo />} />
                  <Route path="/bio" element={<ProfileDescription />} />
                  <Route path="/profile-photo" element={<ProfilePhoto />} />
                  <Route path="/availability" element={<Availability />} />
                  <Route path="/payment-info" element={<DirectDepositInfo />} />
                  <Route path="/herbalist-form" element={<HerbalistForm />} />
                  <Route
                    path="/application-complete"
                    element={<ApplicationComplete />}
                  />
                  <Route
                    path="/practitioner-profile"
                    element={<PractitionerProfile />}
                  />
                  <Route
                    path="/view-practitioners"
                    element={<ViewPractitioners />}
                  />
                  {/* <Route
              path="/practitioner-profile-details/:userId"
              element={<PractitionerProfileDetails />}
            /> */}
                  <Route
                    path="/practitioner-profile-details/:practitionerId"
                    element={<PractitionerProfileDetails />}
                  />
                  <Route
                    path="/scheduling-appointment/:userId"
                    element={<ScheduleAppointment />}
                  />
                  <Route
                    path="/members-scheduling-appointment/:userId"
                    element={<MembersScheduleAppointments />}
                  />
                  <Route
                    path="/rescheduling/:practitionerId"
                    element={<Rescheduling />}
                  />
                  <Route
                    path="/appointment-completion"
                    element={<AppointmentCompletion />}
                  />
                  {/* Clean article URLs: /learn/article/<topic-slug>/<id> (splat so the
                  blogs/ subfolder works). The bare /learn/article route is kept so
                  previously-shared ?topic=...&articleId=... links still resolve
                  (ArticleComponent redirects them to the clean URL). */}
                  <Route
                    path="/learn/article/*"
                    element={<ArticleComponent />}
                  />
                  <Route path="/learn/article" element={<ArticleComponent />} />
                  {/* Admin-authored (Supabase CMS) articles */}
                  <Route path="/learn/post/:slug" element={<DbArticle />} />
                  <Route
                    path="/learn/category/:category"
                    element={<CategoryPage />}
                  />
                  <Route path="/mission" element={<Mission />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/app-waitlist" element={<AppWaitList />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/signUp" element={<SignUp />} />
                  <Route path="/faq" element={<FAQ />} />
                  <Route
                    path="/editorial-process"
                    element={<EditorialProcess />}
                  />
                  <Route path="/forgotPassword" element={<ForgotPassword />} />
                  <Route path="/quizzes" element={<QuizzesHub />} />
                  <Route path="/quiz" element={<DoshaQuiz />} />
                  <Route
                    path="/quiz/dosha/result/:type"
                    element={<DoshaResult />}
                  />
                  <Route path="/quiz/:slug" element={<Quiz />} />
                  <Route
                    path="/quiz/:slug/result/:type"
                    element={<QuizResult />}
                  />
                  <Route path="/herbCategories" element={<HerbCategories />} />
                  <Route
                    path="/herbs/category/:slug"
                    element={<HerbCategoryList />}
                  />
                  <Route path="/herbs/:slug" element={<HerbPage />} />
                  <Route
                    path="/conditions"
                    element={
                      <Suspense
                        fallback={
                          <div style={{ padding: "80px 20px" }}>
                            Loading conditions…
                          </div>
                        }
                      >
                        <ConditionsIndex />
                      </Suspense>
                    }
                  />
                  <Route path="/conditions/:slug" element={<ConditionPage />} />
                  <Route
                    path="/atlas"
                    element={
                      <Suspense
                        fallback={
                          <div style={{ padding: "80px 20px" }}>
                            Loading the Atlas…
                          </div>
                        }
                      >
                        <AtlasPage />
                      </Suspense>
                    }
                  />
                  <Route path="/payment" element={<Payment />} />
                  <Route path="/completion" element={<Completion />} />
                  <Route path="/services" element={<Services />} />
                  <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                  <Route
                    path="/terms-of-service"
                    element={<TermsOfService />}
                  />
                  <Route path="/gender" element={<Gender />} />
                  <Route path="/alcohol" element={<Alcohol />} />
                  <Route path="/allergies" element={<Allergies />} />
                  <Route
                    path="/chronicDiseases"
                    element={<ChronicDiseases />}
                  />
                  <Route path="/chronicPain" element={<ChronicPain />} />
                  <Route
                    path="/currentMedication"
                    element={<CurrentMedication />}
                  />
                  <Route path="/diet" element={<Diet />} />
                  <Route path="/dietRating" element={<DietRating />} />
                  <Route path="/drVisits" element={<DrVisits />} />
                  <Route path="/exercise" element={<Exercise />} />
                  <Route path="/familyHistory" element={<FamilyHistory />} />
                  <Route path="/fruits" element={<FruitsVeggies />} />
                  <Route path="/healthGoal" element={<HealthGoal />} />
                  <Route path="/healthIssues" element={<HealthIssues />} />
                  <Route
                    path="/mentalHealthStatus"
                    element={<MentalHealthStatus />}
                  />
                  <Route
                    path="/mentalHealthSatisfaction"
                    element={<MentalHealthSatisfaction />}
                  />
                  <Route path="/mobilityIssues" element={<MobilityIssues />} />
                  <Route path="/options" element={<Options />} />
                  <Route
                    path="/pregnancyStatus"
                    element={<PregnancyStatus />}
                  />
                  <Route path="/screenTime" element={<ScreenTime />} />
                  <Route path="/sleep" element={<Sleep />} />
                  <Route path="/sleepDisorders" element={<SleepDisorders />} />
                  <Route path="/smoke" element={<Smoke />} />
                  <Route path="/stress" element={<Stress />} />
                  <Route path="/stressSymptoms" element={<StressSymptoms />} />
                  <Route
                    path="/remedy-recommendation"
                    element={<RemedyRecommendation />}
                  />
                  <Route
                    path="/diseaseCategories"
                    element={<DiseaseCategories />}
                  />
                  <Route
                    path="/physicalHealthSatisfaction"
                    element={<PhysicalHealthSatisfaction />}
                  />
                  <Route path="/weight" element={<Weight />} />
                  <Route path="/waterIntake" element={<WaterIntake />} />
                  <Route
                    path="/wellnessPractices"
                    element={<WellnessPractices />}
                  />
                  <Route
                    path="/how-you-heard-about-us"
                    element={<HowDidYouHearAboutUs />}
                  />
                  <Route path="/quizComponent" element={<QuizComponent />} />

                  {/* <Route path="/subscriptionOptions" element={<SubscriptionOptions />} /> */}
                  <Route
                    path="/subscription-payment"
                    element={<SubscriptionPayment />}
                  />
                  {/* <Route path="/order" element={<Order />} /> */}
                  <Route
                    path="/subscriptionOptions"
                    element={<SubscriptionOptions />}
                  />
                  <Route path="/user-profile" element={<UserProfile />} />
                  <Route
                    path="/upcoming-visits"
                    element={<UpcomingAppointments />}
                  />
                  <Route path="/past-visits" element={<PastAppointments />} />
                  <Route path="/user-settings" element={<UserSettings />} />
                  {/********************* practitioners components ***********************/}
                  <Route
                    path="/practitioners-dashboard"
                    element={<PractitionersDashboard />}
                  />
                  <Route
                    path="/appointments-mng"
                    element={<AppointmentManagement />}
                  />
                  <Route
                    path="/meetings-mng"
                    element={<MeetingsManagement />}
                  />
                  <Route path="/meeting-notes" element={<MeetingNote />} />
                  <Route path="/clients-info" element={<ClientsInfo />} />
                  <Route
                    path="/client-details/:id"
                    element={<ClinetsDetails />}
                  />
                  <Route path="/order" element={<Order />} />
                  <Route path="/welcome" element={<Welcome />} />
                  <Route
                    path="/practitioner-settings"
                    element={<PractitionerSettings />}
                  />

                  {/********************* admin components (RequireAdmin) ***********************/}

                  <Route
                    path="/herbs-info"
                    element={
                      <RequireAdmin>
                        <HerbsInfo />
                      </RequireAdmin>
                    }
                  />
                  <Route
                    path="/phytotherapy-index"
                    element={
                      <RequireAdmin>
                        <PhytotherapyIndex />
                      </RequireAdmin>
                    }
                  />
                  <Route
                    path="/add-herbs"
                    element={
                      <RequireAdmin>
                        <AddHerbs />
                      </RequireAdmin>
                    }
                  />
                  <Route
                    path="/herbs-details/:id"
                    element={
                      <RequireAdmin>
                        <HerbsDetails />
                      </RequireAdmin>
                    }
                  />
                  <Route
                    path="/maral1362"
                    element={
                      <RequireAdmin>
                        <AdminDashboard />
                      </RequireAdmin>
                    }
                  />
                  <Route
                    path="/practitioners-info"
                    element={
                      <RequireAdmin>
                        <PractitionerInfo />
                      </RequireAdmin>
                    }
                  />
                  <Route
                    path="/practitioners-details/:id"
                    element={
                      <RequireAdmin>
                        <PractitionersDetails />
                      </RequireAdmin>
                    }
                  />
                  <Route
                    path="/users-info"
                    element={
                      <RequireAdmin>
                        <UsersInfo />
                      </RequireAdmin>
                    }
                  />
                  <Route
                    path="/decryptor"
                    element={
                      <RequireAdmin>
                        <Decryptor />
                      </RequireAdmin>
                    }
                  />
                  <Route
                    path="/add-recipe"
                    element={
                      <RequireAdmin>
                        <AddRecipe />
                      </RequireAdmin>
                    }
                  />
                  <Route
                    path="/recipe-details/:id"
                    element={
                      <RequireAdmin>
                        <RecipeDetails />
                      </RequireAdmin>
                    }
                  />
                  <Route
                    path="/recipes-info"
                    element={
                      <RequireAdmin>
                        <RecipesInfo />
                      </RequireAdmin>
                    }
                  />
                  <Route
                    path="/to-array"
                    element={
                      <RequireAdmin>
                        <ConvertFieldToArray />
                      </RequireAdmin>
                    }
                  />
                  <Route
                    path="/unique-categories"
                    element={
                      <RequireAdmin>
                        <UniqueCategories />
                      </RequireAdmin>
                    }
                  />
                  <Route
                    path="/product-review"
                    element={
                      <RequireAdmin>
                        <ProductReview />
                      </RequireAdmin>
                    }
                  />
                  <Route
                    path="/add-product"
                    element={
                      <RequireAdmin>
                        <AddProduct />
                      </RequireAdmin>
                    }
                  />
                  <Route
                    path="/product-diagnostic"
                    element={
                      <RequireAdmin>
                        <ProductDiagnostic />
                      </RequireAdmin>
                    }
                  />
                  {/********************* Business components ***********************/}
                  <Route path="/demo-request" element={<DemoRequest />} />
                  <Route
                    path="/affiliate-request"
                    element={<AffiliateRequest />}
                  />

                  {/********************* Affiliate program ***********************/}
                  {/*
                Influencer clean link: /r/:tag → Supabase Edge Function.
                In production public/.htaccess 302-redirects this at the server
                (preserving the visitor IP/User-Agent needed for iOS attribution),
                so this route is only a fallback for dev / non-Apache hosts.
              */}
                  <Route path="/r/:tag" element={<InfluencerRedirect />} />
                  {/* Legacy URL — everyone uses /login now. */}
                  <Route
                    path="/affiliate-login"
                    element={<Navigate to="/login" replace />}
                  />
                  <Route
                    path="/affiliate-dashboard"
                    element={
                      <RequireAffiliate>
                        <AffiliateDashboard />
                      </RequireAffiliate>
                    }
                  />
                  <Route
                    path="/admin/affiliate-applications"
                    element={
                      <RequireAdmin>
                        <AffiliateApplications />
                      </RequireAdmin>
                    }
                  />
                  <Route
                    path="/admin/affiliate-networks"
                    element={
                      <RequireAdmin>
                        <AffiliateNetworks />
                      </RequireAdmin>
                    }
                  />
                  <Route
                    path="/admin/onboarding-funnel"
                    element={
                      <RequireAdmin>
                        <AdminOnboardingFunnel />
                      </RequireAdmin>
                    }
                  />
                  <Route
                    path="/admin/contact-messages"
                    element={
                      <RequireAdmin>
                        <ContactMessages />
                      </RequireAdmin>
                    }
                  />
                  <Route
                    path="/admin/articles"
                    element={
                      <RequireAdmin>
                        <ArticlesAdmin />
                      </RequireAdmin>
                    }
                  />
                  <Route
                    path="/admin/articles/new"
                    element={
                      <RequireAdmin>
                        <ArticleEditor />
                      </RequireAdmin>
                    }
                  />
                  <Route
                    path="/admin/articles/edit/:id"
                    element={
                      <RequireAdmin>
                        <ArticleEditor />
                      </RequireAdmin>
                    }
                  />
                  <Route
                    path="/admin/categories"
                    element={
                      <RequireAdmin>
                        <CategoriesAdmin />
                      </RequireAdmin>
                    }
                  />
                  <Route
                    path="/admin/app-waitlist"
                    element={
                      <RequireAdmin>
                        <AppWaitlistAdmin />
                      </RequireAdmin>
                    }
                  />
                  <Route
                    path="/admin/quiz-leads"
                    element={
                      <RequireAdmin>
                        <QuizLeadsAdmin />
                      </RequireAdmin>
                    }
                  />
                  <Route
                    path="/admin/demo-requests"
                    element={
                      <RequireAdmin>
                        <DemoRequestsAdmin />
                      </RequireAdmin>
                    }
                  />

                  {/********************* Video components ***********************/}
                  <Route path="/meeting-room" element={<MeetingRoom />} />
                  <Route
                    path="/video-wrapper"
                    element={
                      <LazyRoute label="the call">
                        <VideoCallWrapper />
                      </LazyRoute>
                    }
                  />
                  {/********************* Explorer components ***********************/}
                  <Route path="/explorer" element={<Dashboard />} />
                  <Route path="/recipes/:categoryName" element={<Recipes />} />
                  <Route path="/recipes/all" element={<RecipesAll />} />
                  <Route path="/herbs/all" element={<HerbsAll />} />
                  <Route path="/view-herbs/" element={<HerbsGrid />} />

                  {/* <Route
                path="/recipes/recommended"
                element={<PersonalizedRecipes />}
              />*/}
                </Routes>
              </Suspense>
              <FooterWrapper />
            </Layout>
          </SubscriptionProvider>
          <QRCodeModal />
        </QRModalProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
